import { useEffect, useRef, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useJob } from '@/hooks/useJobs';
import {
  computeJobCardTotals,
  useSaveJobCardFields,
} from '@/hooks/useJobCardFields';
import {
  JOB_CARD_CUSTOM_ROW_CAPACITY,
  mergeJobCardFields,
  type JobCardCustomLine,
  type JobCardDirectCostLabelKey,
  type JobCardFields,
} from '@jobos/shared';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const formatCurrency = (value: number) => currencyFormatter.format(value);

// Parse a raw input string into a non-negative number; blank/invalid → 0.
function parseNonNegative(raw: string): number {
  const parsed = parseFloat(raw);
  if (Number.isNaN(parsed)) return 0;
  return parsed < 0 ? 0 : parsed;
}

// Number mask: allows empty, digits, and a single decimal point. This is what
// lets intermediate edits like "" or "0." survive while the user is typing.
const NUMERIC_MASK = /^\d*\.?\d*$/;

// Column A of the job card is a fixed-width cell and the schema rejects longer
// names, so stop the keystrokes rather than letting the save 400.
const LABEL_MAX_LENGTH = 40;

interface NumberCellProps {
  value: number;
  ariaLabel: string;
  onChange: (value: number) => void;
  onBlur: () => void;
}

// A plain text input with a numeric mask (no spinner arrows). We hold the raw
// string locally so mid-edit states — a trailing ".", a cleared field — aren't
// clobbered by binding the field to the parsed number, while still lifting the
// parsed value up for cost math.
function NumberCell({ value, ariaLabel, onChange, onBlur }: NumberCellProps) {
  const [text, setText] = useState(() => String(value));

  // Resync the displayed text when `value` changes for a reason other than this
  // input's own keystroke (Reset defaults, job load). We only overwrite when the
  // current text doesn't already represent `value`, so in-progress edits like
  // "0." or "" aren't wiped mid-typing.
  useEffect(() => {
    setText((cur) => (parseNonNegative(cur) === value ? cur : String(value)));
  }, [value]);

  const handleChange = (raw: string) => {
    if (!NUMERIC_MASK.test(raw)) return; // reject non-numeric keystrokes
    setText(raw);
    onChange(parseNonNegative(raw));
  };

  const handleBlur = () => {
    // Normalize the display on commit ("0." → "0", "" → "0", "03" → "3").
    setText(String(value));
    onBlur();
  };

  return (
    <TableCell>
      <Input
        type="text"
        inputMode="decimal"
        aria-label={ariaLabel}
        value={text}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={handleBlur}
        className="h-8 w-24"
      />
    </TableCell>
  );
}

interface LabelCellProps {
  value: string;
  ariaLabel: string;
  /** The template's own name for this row — shown once the user clears the cell. */
  placeholder?: string;
  onChange: (value: string) => void;
  onBlur: () => void;
}

// The row-name cell. Same local-state shape as NumberCell so a Reset defaults or
// a fresh job load resyncs the visible text without clobbering an in-progress edit.
function LabelCell({
  value,
  ariaLabel,
  placeholder,
  onChange,
  onBlur,
}: LabelCellProps) {
  const [text, setText] = useState(value);

  useEffect(() => {
    setText((cur) => (cur === value ? cur : value));
  }, [value]);

  const handleChange = (raw: string) => {
    setText(raw);
    onChange(raw);
  };

  return (
    <TableCell>
      <Input
        type="text"
        aria-label={ariaLabel}
        value={text}
        maxLength={LABEL_MAX_LENGTH}
        placeholder={placeholder}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={onBlur}
        className="h-8 w-48"
      />
    </TableCell>
  );
}

const LABOR_ROWS = [
  { key: 'management', label: 'Management' },
  { key: 'office', label: 'Office' },
  { key: 'assistant', label: 'Assistant' },
] as const;

// Toll and mileage are the reimbursement section's rate × qty rows. They are not
// renameable: the template's own names are what the sheet prints.
const REIMBURSEMENT_ROWS = [
  { key: 'toll', label: 'Toll' },
  { key: 'mileage', label: 'Mileage' },
] as const;

const DIRECT_COST_ROWS = [
  { key: 'parking', label: 'Parking (estimated)' },
  { key: 'gasEstimate', label: 'Gas (estimated)' },
  { key: 'materials', label: 'Materials (estimated)' },
  { key: 'homeDepot', label: 'Home Depot (estimated)' },
] as const;

const blankCustomLine = (): JobCardCustomLine => ({ label: '', rate: 0, qty: 0 });

/**
 * Rebuild `fields` with the reimbursement custom list replaced. Always returns a
 * new array — `JOB_CARD_FIELD_DEFAULTS` hands out shared references, so an
 * in-place push/splice here would corrupt the module-level defaults for the session.
 */
function withCustom(
  prev: JobCardFields,
  update: (lines: JobCardCustomLine[]) => JobCardCustomLine[],
): JobCardFields {
  return {
    ...prev,
    reimbursement: {
      ...prev.reimbursement,
      custom: update(prev.reimbursement.custom),
    },
  };
}

/**
 * A row the user added but never filled in. It exists in local state so there is
 * something to type into, but the generator would print it as a `0 x 0` line, so
 * it must not reach the server.
 */
const isBlankCustomLine = (line: JobCardCustomLine) =>
  line.label.trim() === '' && line.rate === 0 && line.qty === 0;

/**
 * What goes over the wire: local state minus the blank rows. Filtering here and
 * not in state is deliberate — the Add cap counts the rows on screen, so two
 * blank rows still disable Add even though they save as an empty list.
 */
const toSavePayload = (f: JobCardFields): JobCardFields => ({
  ...f,
  reimbursement: {
    ...f.reimbursement,
    custom: f.reimbursement.custom.filter((l) => !isBlankCustomLine(l)),
  },
});

/**
 * A stable string for a set of fields, whatever order their keys arrive in.
 * mergeJobCardFields rebuilds every object key-by-key in a fixed order, so this
 * survives the round-trip through jsonb, which reorders keys.
 */
const canonicalKey = (stored: Partial<JobCardFields> | null | undefined) =>
  JSON.stringify(mergeJobCardFields(stored));

interface CustomLineRowsProps {
  lines: JobCardCustomLine[];
  costs: number[];
  onChange: (index: number, patch: Partial<JobCardCustomLine>) => void;
  onRemove: (index: number) => void;
  onBlur: () => void;
}

function CustomLineRows({
  lines,
  costs,
  onChange,
  onRemove,
  onBlur,
}: CustomLineRowsProps) {
  return (
    <>
      {lines.map((line, index) => {
        const name = `Custom reimbursement line ${index + 1}`;
        return (
          <TableRow key={`reimbursement-custom-${index}`}>
            <LabelCell
              value={line.label}
              ariaLabel={`${name} label`}
              placeholder="Line name"
              onChange={(value) => onChange(index, { label: value })}
              onBlur={onBlur}
            />
            <NumberCell
              value={line.rate}
              ariaLabel={`${name} rate`}
              onChange={(value) => onChange(index, { rate: value })}
              onBlur={onBlur}
            />
            <NumberCell
              value={line.qty}
              ariaLabel={`${name} qty`}
              onChange={(value) => onChange(index, { qty: value })}
              onBlur={onBlur}
            />
            <TableCell>{formatCurrency(costs[index] ?? 0)}</TableCell>
            <TableCell className="text-right">
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Remove ${name.toLowerCase()}`}
                onClick={() => onRemove(index)}
                className="h-7 w-7 p-0"
              >
                <Trash2 className="h-3.5 w-3.5 text-gray-400 hover:text-red-500" />
              </Button>
            </TableCell>
          </TableRow>
        );
      })}
    </>
  );
}

interface JobCardFieldsCardProps {
  jobId: string;
}

export function JobCardFieldsCard({ jobId }: JobCardFieldsCardProps) {
  const { data: job } = useJob(jobId);
  const save = useSaveJobCardFields(jobId);

  const [fields, setFields] = useState<JobCardFields>(() =>
    mergeJobCardFields(job?.jobCardFields),
  );

  // Every payload this card has sent, canonicalized. A successful save patches
  // the server's echo into the job cache, which re-runs the effect below — and
  // re-seeding from our own echo would wipe whatever the user has typed since.
  const sentPayloadKeys = useRef(new Set<string>());

  // Those keys describe ONE job. The Expenses tab unmounts this card when you
  // leave the Labor sub-tab, but routing straight from one job to another keeps
  // it mounted — and job A's keys would then suppress a legitimate load of job
  // B whose fields happen to match.
  const keyedJobId = useRef(jobId);
  if (keyedJobId.current !== jobId) {
    keyedJobId.current = jobId;
    sentPayloadKeys.current = new Set();
  }

  // Re-seed local state when the loaded job's stored fields change (e.g. on
  // initial load, or after another client updates them). Keyed on the MERGED
  // value: jsonb doesn't preserve key order, so raw JSON of the stored value
  // would look "changed" after a round-trip that changed nothing.
  const storedFieldsKey = canonicalKey(job?.jobCardFields);
  useEffect(() => {
    // An echo of something we sent is not a foreign change. Skipping it is what
    // keeps a row added, or a name half-typed, while a save was in flight.
    if (sentPayloadKeys.current.has(storedFieldsKey)) return;
    setFields(mergeJobCardFields(job?.jobCardFields));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedFieldsKey]);

  const totals = computeJobCardTotals(fields);

  // The single save path: filter blanks, remember what went out, send it.
  const commitSave = (next: JobCardFields) => {
    const payload = toSavePayload(next);
    sentPayloadKeys.current.add(canonicalKey(payload));
    save.mutate(payload);
  };

  const handleBlurSave = () => {
    commitSave(fields);
  };

  const handleResetDefaults = () => {
    // A fresh deep copy, never JOB_CARD_FIELD_DEFAULTS itself: its `custom`
    // array is a shared module-level reference and would end up in React state.
    const defaults = mergeJobCardFields(null);
    setFields(defaults);
    commitSave(defaults);
  };

  // A row name that was cleared must be SENT as '' — an absent key means "keep
  // the template's own label", so omitting it would bring the old name back.
  const handleLabelChange = (key: JobCardDirectCostLabelKey, value: string) => {
    setFields((prev) => ({
      ...prev,
      directCostLabels: { ...prev.directCostLabels, [key]: value },
    }));
  };

  // Adding a row changes nothing the server would store — a blank line is
  // filtered out of the payload — so Add is a local edit only, and the row is
  // persisted by the blur-save on the first field the user fills in. Removing a
  // row does change the payload, so it saves right away.
  const handleAddCustom = () => {
    setFields((prev) =>
      withCustom(prev, (lines) =>
        lines.length >= JOB_CARD_CUSTOM_ROW_CAPACITY
          ? lines
          : [...lines, blankCustomLine()],
      ),
    );
  };

  const handleRemoveCustom = (index: number) => {
    const next = withCustom(fields, (lines) =>
      lines.filter((_, i) => i !== index),
    );
    setFields(next);
    commitSave(next);
  };

  const handleCustomFieldChange = (
    index: number,
    patch: Partial<JobCardCustomLine>,
  ) => {
    setFields((prev) =>
      withCustom(prev, (lines) =>
        lines.map((row, i) => (i === index ? { ...row, ...patch } : row)),
      ),
    );
  };

  return (
    <Card>
      <CardContent className="space-y-6 pt-6">
        <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={handleResetDefaults}>
              Reset defaults
            </Button>
          </div>

          <div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Labor</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {LABOR_ROWS.map(({ key, label }) => {
                  const line = fields.labor[key];
                  const cost = totals.labor[key];
                  return (
                    <TableRow key={key}>
                      <TableCell>{label}</TableCell>
                      <NumberCell
                        value={line.rate}
                        ariaLabel={`${label} rate`}
                        onChange={(value) =>
                          setFields((prev) => ({
                            ...prev,
                            labor: {
                              ...prev.labor,
                              [key]: { ...prev.labor[key], rate: value },
                            },
                          }))
                        }
                        onBlur={handleBlurSave}
                      />
                      <NumberCell
                        value={line.hours}
                        ariaLabel={`${label} hours`}
                        onChange={(value) =>
                          setFields((prev) => ({
                            ...prev,
                            labor: {
                              ...prev.labor,
                              [key]: { ...prev.labor[key], hours: value },
                            },
                          }))
                        }
                        onBlur={handleBlurSave}
                      />
                      <TableCell>{formatCurrency(cost)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={3} className="text-right font-semibold">
                    Total
                  </TableCell>
                  <TableCell className="font-semibold">
                    {formatCurrency(totals.laborTotal)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </div>

          <div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reimbursement</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {REIMBURSEMENT_ROWS.map(({ key, label }) => {
                  const line = fields.reimbursement[key];
                  const cost = totals.reimbursement[key];
                  return (
                    <TableRow key={key}>
                      <TableCell>{label}</TableCell>
                      <NumberCell
                        value={line.rate}
                        ariaLabel={`${label} rate`}
                        onChange={(value) =>
                          setFields((prev) => ({
                            ...prev,
                            reimbursement: {
                              ...prev.reimbursement,
                              [key]: { ...prev.reimbursement[key], rate: value },
                            },
                          }))
                        }
                        onBlur={handleBlurSave}
                      />
                      <NumberCell
                        value={line.qty}
                        ariaLabel={`${label} qty`}
                        onChange={(value) =>
                          setFields((prev) => ({
                            ...prev,
                            reimbursement: {
                              ...prev.reimbursement,
                              [key]: { ...prev.reimbursement[key], qty: value },
                            },
                          }))
                        }
                        onBlur={handleBlurSave}
                      />
                      <TableCell>{formatCurrency(cost)}</TableCell>
                      <TableCell />
                    </TableRow>
                  );
                })}
                {DIRECT_COST_ROWS.map(({ key, label }) => (
                  <TableRow key={key}>
                    <LabelCell
                      value={fields.directCostLabels?.[key] ?? label}
                      ariaLabel={`${label} label`}
                      placeholder={label}
                      onChange={(value) => handleLabelChange(key, value)}
                      onBlur={handleBlurSave}
                    />
                    <TableCell />
                    <TableCell />
                    <NumberCell
                      value={fields.directCosts[key]}
                      ariaLabel={`${label} cost`}
                      onChange={(value) =>
                        setFields((prev) => ({
                          ...prev,
                          directCosts: { ...prev.directCosts, [key]: value },
                        }))
                      }
                      onBlur={handleBlurSave}
                    />
                    <TableCell />
                  </TableRow>
                ))}
                <CustomLineRows
                  lines={fields.reimbursement.custom}
                  costs={totals.reimbursementCustom}
                  onChange={handleCustomFieldChange}
                  onRemove={handleRemoveCustom}
                  onBlur={handleBlurSave}
                />
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={3} className="text-right font-semibold">
                    Total
                  </TableCell>
                  <TableCell className="font-semibold">
                    {formatCurrency(totals.reimbursementTotal)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableFooter>
            </Table>
            <div className="mt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={
                  fields.reimbursement.custom.length >= JOB_CARD_CUSTOM_ROW_CAPACITY
                }
                onClick={handleAddCustom}
              >
                <Plus className="mr-1 h-4 w-4" />
                Add reimbursement line
              </Button>
            </div>
          </div>
      </CardContent>
    </Card>
  );
}
